import React, { useEffect, useReducer } from 'react';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { getError, formatEuro } from '../utils';
import Card from 'react-bootstrap/Card';
import { Container } from 'react-bootstrap';
import Pagination from '../components/general/Pagination';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        zones: action.payload.zones,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        loadingCreate: false,
      };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };

    case 'DELETE_REQUEST':
      return { ...state, loadingDelete: true, successDelete: false };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case 'DELETE_FAIL':
      return { ...state, loadingDelete: false, successDelete: false };
    case 'DELETE_RESET':
      return { ...state, loadingDelete: false, successDelete: false };

    default:
      return state;
  }
};

export default function ZoneListScreen() {
  const [
    {
      loading,
      error,
      zones,
      loadingCreate,
      loadingDelete,
      successDelete,
      pages,
    },
    dispatch,
  ] = useReducer(reducer, {
    loading: true,
    error: '',
    zones: [],
  });

  const navigate = useNavigate();

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await api.get(`/api/zones?page=${page}`);

        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    }; // fetches the data of all molds from the backend

    if (successDelete) {
      dispatch({ type: 'DELETE_RESET' });
    } else {
      fetchData();
    }
  }, [successDelete, page]);

  const createHandler = async () => {
    if (window.confirm('Do you really want to create a new shipping zone?')) {
      try {
        dispatch({ type: 'CREATE_REQUEST' });
        const { data } = await api.post('/api/zones', {});
        toast.success('Zone created.');
        dispatch({ type: 'CREATE_SUCCESS' });
        navigate(`/admin/zone/${data.zone._id}`);
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: 'CREATE_FAIL',
        });
      }
    }
  };

  const deleteHandler = async (zone) => {
    if (window.confirm('Do you really want to delete this zone?')) {
      try {
        await api.delete(`/api/zones/${zone._id}`);
        toast.success('Shipping Zone deleted.');
        dispatch({ type: 'DELETE_SUCCESS' });
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: 'DELETE_FAIL',
        });
      }
    }
  };

  return (
    <Container>
      <div>
        <title>Zones</title>

        <h1 className="screen-heading">Zones</h1>

        {loading || loadingCreate || loadingDelete ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <Row>
              <Col>
                <div className="d-flex justify-content-center mb-5">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={createHandler}
                  >
                    Create Zone
                  </Button>
                </div>
              </Col>
            </Row>
            <Row className="d-xl-none">
              {zones?.map((zone) => (
                <Col key={zone._id} xs={12} sm={12} md={12} lg={6}>
                  <Card className="products-card">
                    <Card.Body className="text-center">
                      <Card.Title className="code mb-4">
                        {zone.names.en}
                      </Card.Title>
                      <div className="divider"></div>
                      <div className="code-info">
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Price:
                          </strong>{' '}
                          <span className="no-wrap">
                            {formatEuro(zone.price)}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Options:
                          </strong>
                          <span className="no-wrap w-100 mt-1">
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() =>
                                navigate(`/admin/zone/${zone._id}`)
                              }
                            >
                              Edit
                            </Button>
                            &nbsp;
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() => deleteHandler(zone)}
                            >
                              Delete
                            </Button>
                          </span>
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="d-none d-xl-block">
              <table className="table">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>PRICE</th>
                    <th>DHL</th>
                    <th>OPTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {zones?.map((zone) => (
                    <tr key={zone._id}>
                      <td>{zone.names.en}</td>
                      <td>{formatEuro(zone.price)}</td>
                      <td>
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => navigate(`/admin/zone/${zone._id}`)}
                        >
                          Edit
                        </Button>
                        &nbsp;
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => deleteHandler(zone)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              pages={pages}
              page={Number(page)}
              baseUrl="/admin/zones"
              search={null}
            />
          </>
        )}
      </div>
    </Container>
  );
}
